"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import NumberFlow from "@number-flow/react";
import { Modal, ModalContent, ModalTitle } from "@/components/ui/modal";
import { SubmitButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Icon } from "@/components/ui/icon";
import { PlanIcon } from "@/components/ui/plan-icon";
import { showToast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import { customInstance } from "@/lib/api/client";
import { getStoredCometToken } from "@/lib/cometly";
import { CountrySelect } from "@/components/ui/country-select";
import { OptionBadge } from "@/components/ui/option-badge";
import { AddressAutocomplete } from "./address-autocomplete";
import { RegionSelect, getRegionLabel } from "./region-select";
import { SubscribeSuccess } from "./subscribe-success";
import { Select } from "@/components/ui/select";
import {
  PaymentIcon,
  type PaymentType,
} from "react-svg-credit-card-payment-icons";
import { useAuth } from "@/features/auth";
import {
  StripeElementsProvider,
  ELEMENT_STYLE,
} from "./stripe-elements-provider";
import {
  useBillingDetails,
  useSavedPaymentMethods,
  useDeletePaymentMethod,
  useValidateCoupon,
  type PlanResponse,
  type AppliedCoupon,
} from "../hooks/use-subscription";
import {
  confirmAndAwaitActive,
  type SubscribeOutcome,
} from "../lib/confirm-subscription";

/* ─── Types ─── */

interface SubscribeModalProps {
  open: boolean;
  onClose: () => void;
  plan: PlanResponse;
  initialCadence: "monthly" | "annual";
  initialSeatCount: number;
}

interface BillingDetails {
  companyName: string;
  email: string;
  cardName: string;
  country: string;
  line1: string;
  city: string;
  state: string;
  postalCode: string;
}

/* ─── Helpers ─── */

const POSTAL_PLACEHOLDERS: Record<string, string> = {
  US: "94105",
  CA: "K1A 0B1",
  GB: "SW1A 1AA",
  AU: "2000",
  DE: "10115",
  FR: "75008",
  ES: "28013",
  IT: "00184",
  NL: "1011 AC",
  IN: "110001",
  JP: "100-0001",
  BR: "01310-100",
  MX: "01000",
  IE: "D02 AF30",
};

function getPostalPlaceholder(country: string): string {
  return POSTAL_PLACEHOLDERS[country] ?? "Postal code";
}

/** Discount in cents the coupon applies to a given subtotal (cents). */
function couponDiscountCents(
  coupon: AppliedCoupon | null,
  subtotalCents: number,
): number {
  if (!coupon) return 0;
  if (coupon.percent_off) {
    return Math.round((subtotalCents * coupon.percent_off) / 100);
  }
  if (coupon.amount_off_cents) {
    return Math.min(coupon.amount_off_cents, subtotalCents);
  }
  return 0;
}

/** Short human label for the coupon's value, e.g. "20% off" or "$10 off". */
function couponValueLabel(coupon: AppliedCoupon): string {
  if (coupon.percent_off) return `${coupon.percent_off}% off`;
  if (coupon.amount_off_cents)
    return `${formatCents(coupon.amount_off_cents)} off`;
  return "Discount";
}

/** How long the discount lasts — derived from the Stripe coupon, e.g.
 *  "first month", "first 3 months", "first year", "lifetime". */
function couponDurationLabel(
  coupon: AppliedCoupon,
  cadence: "monthly" | "annual",
): string {
  if (coupon.duration === "forever") return "lifetime";
  if (coupon.duration === "once") {
    return cadence === "annual" ? "first year" : "first month";
  }
  const months = coupon.duration_in_months ?? 0;
  if (months <= 0) return "limited time";
  if (months % 12 === 0) {
    const years = months / 12;
    return years === 1 ? "first year" : `first ${years} years`;
  }
  return months === 1 ? "first month" : `first ${months} months`;
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function getPerSeatMonthly(
  plan: PlanResponse,
  cadence: "monthly" | "annual",
): number {
  if (cadence === "annual")
    return Math.round((plan.price_annual_cents ?? 0) / 12);
  return plan.price_monthly_cents ?? 0;
}

const PLAN_GRADIENTS: Record<string, string> = {
  solo: "rgba(84, 245, 237, 0.32)",
  growth: "rgba(255, 175, 83, 0.32)",
  business: "rgba(43, 93, 243, 0.32)",
};

/* Muted plan-colored bottom border for the sidebar header (matches Figma) */
const PLAN_BORDERS: Record<string, string> = {
  solo: "#36585C",
  growth: "#5C4F36",
  business: "#36405C",
};

/* ─── Main Modal ─── */

export function SubscribeModal({
  open,
  onClose,
  plan,
  initialCadence,
  initialSeatCount,
}: SubscribeModalProps) {
  const [cadence, setCadence] = useState(initialCadence);
  const [seats, setSeats] = useState(initialSeatCount);
  const [dialogOpen, setDialogOpen] = useState(true);
  const [success, setSuccess] = useState(false);
  const closedRef = useRef(!open);

  const pricePerSeat = getPerSeatMonthly(plan, cadence);
  const totalMonthly = pricePerSeat * seats;

  useEffect(() => {
    closedRef.current = !open;
    setDialogOpen(open);
    if (open) setSuccess(false);
  }, [open]);

  useEffect(() => {
    if (open) {
      setCadence(initialCadence);
      setSeats(initialSeatCount);
    }
  }, [open, initialCadence, initialSeatCount]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        closedRef.current = true;
        setDialogOpen(false);
        setTimeout(onClose, 250);
      }
    },
    [onClose],
  );

  const isClosed = useCallback(() => closedRef.current, []);

  return (
    <>
      <Modal open={dialogOpen && !success} onOpenChange={handleOpenChange}>
        <ModalContent
          size="xl"
          className="flex-col lg:flex-row lg:max-w-[800px] lg:h-[720px] p-0 overflow-y-auto lg:overflow-hidden"
        >
          <ModalTitle className="sr-only">Upgrade to {plan.name}</ModalTitle>
          <StripeElementsProvider amount={totalMonthly}>
            <SubscribeInner
              plan={plan}
              cadence={cadence}
              setCadence={setCadence}
              seats={seats}
              setSeats={setSeats}
              totalMonthly={totalMonthly}
              onClose={() => handleOpenChange(false)}
              isClosed={isClosed}
              onSuccess={() => setSuccess(true)}
            />
          </StripeElementsProvider>
        </ModalContent>
      </Modal>

      <SubscribeSuccess
        open={success}
        plan={plan}
        onClose={() => {
          closedRef.current = true;
          setSuccess(false);
          setDialogOpen(false);
          setTimeout(onClose, 250);
        }}
      />
    </>
  );
}

/* ─── Inner (needs Stripe context) ─── */

interface InnerProps {
  plan: PlanResponse;
  cadence: "monthly" | "annual";
  setCadence: (c: "monthly" | "annual") => void;
  seats: number;
  setSeats: (n: number) => void;
  totalMonthly: number;
  onClose: () => void;
  isClosed: () => boolean;
  onSuccess: () => void;
}

function SubscribeInner({
  plan,
  cadence,
  setCadence,
  seats,
  setSeats,
  totalMonthly,
  onClose,
  isClosed,
  onSuccess,
}: InnerProps) {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const { data: savedMethods = [], isLoading: methodsLoading } =
    useSavedPaymentMethods();
  const deletePaymentMethod = useDeletePaymentMethod();
  const { organization, user } = useAuth();
  const validateCoupon = useValidateCoupon();
  const [deletingPmId, setDeletingPmId] = useState<string | null>(null);
  const [customerReady, setCustomerReady] = useState(false);

  const [isBusy, setIsBusy] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [selectedPmId, setSelectedPmId] = useState<string | null>(null);
  const [showNewCardInput, setShowNewCardInput] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(
    null,
  );

  const { data: existingDetails } = useBillingDetails();
  const [billing, setBilling] = useState<BillingDetails>({
    companyName: organization?.name ?? "",
    email: user?.email ?? "",
    cardName: "",
    country: "US",
    line1: "",
    city: "",
    state: "",
    postalCode: "",
  });

  useEffect(() => {
    if (existingDetails) {
      setBilling((prev) => ({
        ...prev,
        companyName: existingDetails.company_name || prev.companyName,
        email: existingDetails.email || prev.email,
        country: existingDetails.country || prev.country,
        line1: existingDetails.line1 || prev.line1,
        city: existingDetails.city || prev.city,
        state: existingDetails.state || prev.state,
        postalCode: existingDetails.postal_code || prev.postalCode,
      }));
    }
  }, [existingDetails]);

  const totalForPeriod =
    cadence === "annual" ? totalMonthly * 12 : totalMonthly;
  const discountCents = couponDiscountCents(appliedCoupon, totalForPeriod);
  const totalAfterDiscount = Math.max(0, totalForPeriod - discountCents);
  // A "forever" coupon keeps discounting every cycle; anything else (once /
  // repeating) eventually reverts to full price at renewal.
  const renewalCents =
    appliedCoupon?.duration === "forever" ? totalAfterDiscount : totalForPeriod;
  const potentialAnnualSavings =
    ((plan.price_monthly_cents ?? 0) * 12 - (plan.price_annual_cents ?? 0)) *
    seats;
  const maxSeats = plan.max_seats ?? 50;

  const annualPerSeatMonthly =
    Math.round((plan.price_annual_cents ?? 0) / 12) / 100;
  const monthlyPerSeat = (plan.price_monthly_cents ?? 0) / 100;

  const updateBilling = useCallback(
    (field: keyof BillingDetails, value: string) =>
      setBilling((prev) => ({ ...prev, [field]: value })),
    [],
  );

  useEffect(() => {
    customInstance("/api/billing/ensure-customer", { method: "POST" })
      .then(async () => {
        await queryClient.invalidateQueries({
          queryKey: ["billing", "payment-methods"],
        });
      })
      .catch(() => {})
      .finally(() => setCustomerReady(true));
  }, [queryClient]);

  // Payment methods are settled once the customer is ensured and the list query
  // has finished its initial load — only then do we pick the default state so
  // the form doesn't flash "new card" before saved cards arrive.
  const paymentReady = customerReady && !methodsLoading;

  useEffect(() => {
    if (!paymentReady) return;
    if (savedMethods.length > 0 && !selectedPmId) {
      setSelectedPmId(savedMethods[0].id);
      setShowNewCardInput(false);
    } else if (savedMethods.length === 0) {
      setShowNewCardInput(true);
    }
  }, [paymentReady, savedMethods, selectedPmId]);

  // Drop an applied coupon when the user switches to a cadence/plan it doesn't
  // allow (restrictions come from coupon metadata, so nothing is hard-coded).
  useEffect(() => {
    if (!appliedCoupon) return;
    if (
      appliedCoupon.restricted_cadence &&
      appliedCoupon.restricted_cadence !== cadence
    ) {
      setAppliedCoupon(null);
      setDiscountCode("");
      showToast.info("Discount removed", {
        description: `That code is only valid on ${appliedCoupon.restricted_cadence} billing.`,
      });
    }
  }, [cadence, appliedCoupon]);

  const usingNewCard = showNewCardInput || savedMethods.length === 0;

  const canSubscribe =
    !isBusy &&
    !!stripe &&
    billing.companyName.trim() !== "" &&
    (usingNewCard ? cardComplete : !!selectedPmId);

  const invalidateBillingCaches = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["billing"] }),
      queryClient.invalidateQueries({ queryKey: ["auth"] }),
    ]);

  const handleSubscribe = async () => {
    if (billing.companyName.trim() === "") {
      showToast.error("Company name is required.");
      return;
    }
    setIsBusy(true);

    try {
      let pmId = selectedPmId;

      if (usingNewCard) {
        if (!stripe || !elements) {
          showToast.error("Payment form not loaded.");
          setIsBusy(false);
          return;
        }
        const cardNumber = elements.getElement(CardNumberElement);
        if (!cardNumber) {
          showToast.error("Card details not loaded.");
          setIsBusy(false);
          return;
        }

        const { error: pmError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardNumber,
            billing_details: {
              name: billing.cardName || billing.companyName,
              email: billing.email,
              address: {
                country: billing.country,
                line1: billing.line1,
                city: billing.city,
                state: billing.state,
                postal_code: billing.postalCode,
              },
            },
          });

        if (pmError) {
          showToast.error("Card error", {
            description: pmError.message ?? "We couldn't validate your card.",
          });
          setIsBusy(false);
          return;
        }

        await customInstance("/api/billing/payment-methods/attach", {
          method: "POST",
          body: JSON.stringify({ payment_method_id: paymentMethod.id }),
          headers: { "Content-Type": "application/json" },
        });
        await queryClient.invalidateQueries({
          queryKey: ["billing", "payment-methods"],
        });
        pmId = paymentMethod.id;
      }

      const res = await customInstance<SubscribeOutcome>("/api/billing/subscribe", {
        method: "POST",
        body: JSON.stringify({
          plan_slug: plan.slug,
          billing_cadence: cadence,
          seat_count: seats,
          payment_method_id: pmId,
          discount_code: appliedCoupon?.code ?? null,
          company_name: billing.companyName || null,
          billing_email: billing.email || null,
          address_country: billing.country || null,
          address_line1: billing.line1 || null,
          address_city: billing.city || null,
          address_state: billing.state || null,
          address_postal_code: billing.postalCode || null,
          // No pixel in the logged-in app — reuse the token captured at
          // register/login so the checkout conversion stitches to the click.
          comet_token: getStoredCometToken() ?? null,
        }),
        headers: { "Content-Type": "application/json" },
      });

      const outcome = await confirmAndAwaitActive(stripe, res, pmId, plan.slug);
      if (outcome.status === "error") {
        showToast.error("Payment confirmation failed", {
          description: outcome.error,
        });
        setIsBusy(false);
        return;
      }
      if (outcome.status === "pending") {
        showToast.info("Payment received", {
          description:
            "Your payment went through. Refresh billing if your plan is not visible yet.",
        });
        setIsBusy(false);
        return;
      }

      if (isClosed()) {
        setIsBusy(false);
        return;
      }
      await invalidateBillingCaches();
      setIsBusy(false);
      if (isClosed()) return;
      onSuccess();
    } catch (err: unknown) {
      const isApiError =
        err != null &&
        typeof err === "object" &&
        "status" in err &&
        "code" in err;
      const raw = err instanceof Error ? err.message : "";
      const isCardError =
        isApiError && (err as { status: number }).status === 402;
      const title = isCardError ? "Payment declined" : "Subscription failed";
      const description =
        raw && !/internal server error/i.test(raw)
          ? raw
          : "Something went wrong. Please try again.";
      showToast.error(title, { description });
      setIsBusy(false);
    }
  };

  const handleApplyCoupon = async () => {
    const code = discountCode.trim();
    if (!code || validateCoupon.isPending) return;
    try {
      const coupon = await validateCoupon.mutateAsync({
        code,
        planSlug: plan.slug,
        billingCadence: cadence,
      });
      setAppliedCoupon(coupon);
      showToast.success("Discount applied", {
        description: `${couponValueLabel(coupon)} for the ${couponDurationLabel(coupon, cadence)}.`,
      });
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : "";
      showToast.error("Invalid discount code", {
        description:
          raw && !/internal server error/i.test(raw)
            ? raw
            : "That discount code is invalid or expired.",
      });
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountCode("");
  };

  const handleDeleteCard = async (pmId: string) => {
    setDeletingPmId(pmId);
    try {
      await deletePaymentMethod.mutateAsync(pmId);
      if (selectedPmId === pmId) setSelectedPmId(null);
      showToast.success("Payment method deleted");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Please try again.";
      showToast.error("Couldn't remove card", {
        description: /internal server error/i.test(message)
          ? "Please try again."
          : message,
      });
    } finally {
      setDeletingPmId(null);
    }
  };

  return (
    <>
      {/* ─── Left sidebar — order summary ─── */}
      <div className="w-full lg:w-[301px] shrink-0 bg-surface-header border-b lg:border-b-0 lg:border-r border-border flex flex-col">
        {/* Header with plan-colored gradient + subtle grid */}
        <div
          className="relative px-4 pt-4 pb-5 border-b overflow-hidden"
          style={{ borderBottomColor: PLAN_BORDERS[plan.slug] ?? "#36405C" }}
        >
          {/* Grid texture (bottom layer, faded out toward the bottom) */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.054) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.054) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
              backgroundPosition: "-1px -1px",
            }}
          />
          {/* Plan-colored linear gradient on top — washes over and fades the grid */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(180deg, rgba(23,23,23,0) 25.8%, ${
                PLAN_GRADIENTS[plan.slug] ?? "rgba(255,255,255,0.08)"
              } 100%)`,
            }}
          />
          {/* Mobile close — absolute top-right of the plan header */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="lg:hidden absolute right-3 top-3 z-10 flex size-7 items-center justify-center rounded-md text-contrast/50 transition-colors hover:bg-contrast/10 hover:text-contrast/80 cursor-pointer"
          >
            <Icon name="IconCrossMedium" size={16} />
          </button>

          <div className="relative flex flex-col gap-3">
            <PlanIcon
              slug={plan.slug}
              width={40}
              height={40}
              className="size-10"
            />
            <div className="flex flex-col gap-0.5">
              <p className="text-base font-semibold text-heading tracking-[-0.3px]">
                Upgrade to {plan.name}
              </p>
              <p className="text-sm text-contrast/50 leading-5">
                Add your billing details and a payment method to get started.
              </p>
            </div>
          </div>
        </div>

        {/* Summary content */}
        <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
          <div className="flex flex-col min-h-full px-6 py-5">
            <div className="flex flex-col gap-4">
              {/* Billing period */}
              <div className="flex flex-col gap-1">
                <Label>Billing period</Label>
                <BillingPeriodSelect
                  cadence={cadence}
                  onCadenceChange={setCadence}
                  annualPerSeatMonthly={annualPerSeatMonthly}
                  monthlyPerSeat={monthlyPerSeat}
                />
              </div>

              {/* Seats */}
              <SeatsStepper
                seats={seats}
                maxSeats={maxSeats}
                onSeatsChange={setSeats}
              />
            </div>

            {/* Bottom group: discount + totals */}
            <div className="mt-auto pt-5 flex flex-col gap-4">
              {/* Discount code */}
              <div className="flex flex-col gap-1">
                <Label>Discount Code</Label>
                {appliedCoupon ? (
                  <div className="flex items-center">
                    <OptionBadge.Container
                      variant="secondary"
                      size="md"
                      className="gap-1.5 py-1 pl-2 pr-1.5 border border-[#42BA82]/15 bg-[#42BA82]/10 text-[#42BA82]"
                    >
                      <Icon name="IconTag" size={12} className="shrink-0" />
                      <OptionBadge.Name className="text-xs font-medium">
                        {appliedCoupon.code} · {couponValueLabel(appliedCoupon)}{" "}
                        {couponDurationLabel(appliedCoupon, cadence)}
                      </OptionBadge.Name>
                      <OptionBadge.RemoveButton
                        onClick={handleRemoveCoupon}
                        aria-label="Remove discount"
                        className="[&_svg]:text-[#42BA82]"
                      />
                    </OptionBadge.Container>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 h-10 rounded-md border border-contrast/[0.15] bg-contrast/3 pl-3 pr-1.5 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
                    <Icon
                      name="IconTag"
                      size={18}
                      className="text-heading opacity-30 shrink-0"
                    />
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleApplyCoupon();
                        }
                      }}
                      placeholder="Enter code"
                      className="flex-1 bg-transparent text-sm text-heading placeholder:text-contrast/30 outline-none min-w-0"
                    />
                    <SubmitButton
                      type="button"
                      variant="secondary"
                      size="xs"
                      onClick={handleApplyCoupon}
                      loading={validateCoupon.isPending}
                      disabled={!discountCode.trim()}
                    >
                      Apply
                    </SubmitButton>
                  </div>
                )}
              </div>

              {/* Price summary */}
              <div className="flex flex-col gap-3 text-sm rounded-md border border-contrast/[0.15] bg-transparent px-3.5 py-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
                <div className="flex justify-between text-contrast/70">
                  <span>{plan.name}</span>
                  <span className="tabular-nums">
                    <NumberFlow
                      value={totalForPeriod / 100}
                      format={{ style: "currency", currency: "USD" }}
                    />
                  </span>
                </div>
                <div className="flex flex-col gap-2 pb-3 border-b border-contrast/10">
                  <div className="flex justify-between text-contrast/70">
                    <span>Tax</span>
                    <span className="tabular-nums">{formatCents(0)}</span>
                  </div>
                  <div className="flex justify-between text-contrast/70">
                    <span>Subtotal</span>
                    <span className="tabular-nums">
                      <NumberFlow
                        value={totalForPeriod / 100}
                        format={{ style: "currency", currency: "USD" }}
                      />
                    </span>
                  </div>
                  {appliedCoupon && discountCents > 0 && (
                    <div className="flex justify-between gap-2 text-[#42BA82]">
                      <span className="truncate">
                        Discount
                        <span className="text-[#42BA82]/60">
                          {" · "}
                          {couponDurationLabel(appliedCoupon, cadence)}
                        </span>
                      </span>
                      <span className="tabular-nums shrink-0">
                        −
                        <NumberFlow
                          value={discountCents / 100}
                          format={{ style: "currency", currency: "USD" }}
                        />
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex justify-between font-medium text-heading">
                  <span>Total due today</span>
                  <span className="tabular-nums">
                    <NumberFlow
                      value={totalAfterDiscount / 100}
                      format={{ style: "currency", currency: "USD" }}
                    />
                  </span>
                </div>
                {renewalCents !== totalAfterDiscount && (
                  <div className="flex justify-between text-contrast/55">
                    <span>Total at renewal</span>
                    <span className="tabular-nums">
                      <NumberFlow
                        value={renewalCents / 100}
                        format={{ style: "currency", currency: "USD" }}
                      />
                    </span>
                  </div>
                )}
                {/* Savings line — both states stay mounted and crossfade so
                  toggling cadence animates and never shifts the layout. */}
                {potentialAnnualSavings > 0 && (
                  <div className="relative h-[18px]">
                    {/* Annual: confirmation (gold) */}
                    <motion.div
                      animate={{ opacity: cadence === "annual" ? 1 : 0 }}
                      transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                      style={{
                        pointerEvents: cadence === "annual" ? "auto" : "none",
                      }}
                      className="absolute inset-0 flex items-center gap-1.5 text-[13px] font-medium"
                    >
                      <Icon
                        name="IconCircleCheck"
                        fill="filled"
                        size={14}
                        className="shrink-0 text-[#FFAF53]"
                      />
                      <span className="text-[#FFAF53]">
                        You&apos;re saving{" "}
                        <NumberFlow
                          value={potentialAnnualSavings / 100}
                          format={{ style: "currency", currency: "USD" }}
                          className="tabular-nums"
                        />{" "}
                        per year
                      </span>
                    </motion.div>

                    {/* Monthly: clickable nudge (gold) */}
                    <motion.button
                      type="button"
                      onClick={() => setCadence("annual")}
                      animate={{ opacity: cadence === "annual" ? 0 : 1 }}
                      transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                      style={{
                        pointerEvents: cadence === "annual" ? "none" : "auto",
                      }}
                      className="group absolute inset-0 flex items-center gap-1.5 text-[13px] font-medium text-[#FFAF53] hover:text-[#f0a94f] transition-colors cursor-pointer w-fit"
                    >
                      <Icon name="IconTag" size={14} className="shrink-0" />
                      <span>
                        Go annual &amp; save{" "}
                        <NumberFlow
                          value={potentialAnnualSavings / 100}
                          format={{ style: "currency", currency: "USD" }}
                          className="tabular-nums"
                        />
                      </span>
                      <Icon
                        name="IconArrowRight"
                        size={13}
                        className="shrink-0 transition-transform group-hover:translate-x-0.5"
                      />
                    </motion.button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Right column — form ─── */}
      <div className="bg-surface-elevated flex flex-col lg:flex-1 lg:h-full min-w-0">
        {/* Header — sticky on mobile so it stays pinned while the drawer scrolls */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 h-[60px] shrink-0 bg-surface-elevated border-b border-border lg:static lg:z-auto lg:border-b-0">
          <div className="flex items-center gap-1.5">
            <Icon
              name="IconCreditCard2"
              size={18}
              className="text-heading opacity-70"
            />
            <span className="text-base font-semibold text-heading">
              Billing &amp; Payment
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="hidden lg:flex items-center justify-center size-7 rounded-md text-contrast/40 hover:text-contrast/70 hover:bg-contrast/6 transition-colors cursor-pointer"
          >
            <Icon name="IconCrossMedium" size={16} />
          </button>
        </div>

        <div className="hidden lg:block h-px bg-contrast/10 shrink-0 mx-6" />

        {/* Body — on mobile this must stay plain block flow: an un-gated
            overflow-x-hidden forces overflow-y to compute to `auto`, turning it
            into a min-h-0 scroll container that collapses inside the drawer and
            hides everything past the first fields. */}
        <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:overflow-x-hidden px-6 py-5">
          <div className="flex flex-col gap-5">
            {/* Company name */}
            <div className="flex flex-col gap-0.5">
              <Label className="gap-0.5">
                Company Name<span className="text-destructive">*</span>
              </Label>
              <Input
                type="text"
                variant="default"
                value={billing.companyName}
                onChange={(e) => updateBilling("companyName", e.target.value)}
                placeholder="Acme Inc."
              />
            </div>

            {/* Country / Region */}
            <div className="flex gap-3 w-full">
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <Label>Country</Label>
                <CountrySelect
                  value={billing.country}
                  onChange={(code) => updateBilling("country", code)}
                  variant="default"
                />
              </div>
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <Label>{getRegionLabel(billing.country)}</Label>
                <RegionSelect
                  countryCode={billing.country}
                  value={billing.state}
                  onChange={(v) => updateBilling("state", v)}
                />
              </div>
            </div>

            {/* Address */}
            <div className="flex flex-col gap-0.5">
              <Label>Address</Label>
              <AddressAutocomplete
                value={billing.line1}
                country={billing.country}
                onChange={(line1) => updateBilling("line1", line1)}
                onSelect={(addr) =>
                  setBilling((prev) => ({
                    ...prev,
                    line1: addr.line1,
                    city: addr.city || prev.city,
                    state: addr.state || prev.state,
                    postalCode: addr.postalCode || prev.postalCode,
                    country: addr.country || prev.country,
                  }))
                }
                placeholder="123 Main St"
              />
            </div>

            {/* City / Postal code */}
            <div className="flex gap-3 w-full">
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <Label>City</Label>
                <div className="relative">
                  <Icon
                    name="IconBuildings"
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-contrast/40 pointer-events-none"
                  />
                  <Input
                    type="text"
                    variant="default"
                    value={billing.city}
                    onChange={(e) => updateBilling("city", e.target.value)}
                    placeholder="San Francisco"
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <Label>Postal Code</Label>
                <div className="relative">
                  <Icon
                    name="IconHashtag"
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-contrast/40 pointer-events-none"
                  />
                  <Input
                    type="text"
                    variant="default"
                    value={billing.postalCode}
                    onChange={(e) =>
                      updateBilling("postalCode", e.target.value)
                    }
                    placeholder={getPostalPlaceholder(billing.country)}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            <div className="h-px bg-contrast/10" />

            {/* Payment information */}
            <div className="flex flex-col gap-3">
              <Label>Payment information</Label>

              {!paymentReady ? (
                <div className="flex flex-col gap-1.5">
                  {[0, 1].map((i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2.5 h-12 px-3 rounded-md border border-contrast/10 bg-contrast/[0.02]"
                      style={{ opacity: i === 0 ? 1 : 0.55 }}
                    >
                      <div
                        className="h-4.5 w-7 rounded bg-contrast/7 shimmer shimmer-bg text-foreground"
                        style={{ animationDelay: `${i * 120}ms` }}
                      />
                      <div className="flex flex-col gap-1.5 flex-1">
                        <div
                          className="h-2.5 rounded bg-contrast/7 shimmer shimmer-bg text-foreground"
                          style={{
                            width: i === 0 ? "60%" : "48%",
                            animationDelay: `${i * 120 + 60}ms`,
                          }}
                        />
                        <div
                          className="h-2 rounded bg-contrast/5 shimmer shimmer-bg text-foreground"
                          style={{
                            width: i === 0 ? "38%" : "30%",
                            animationDelay: `${i * 120 + 120}ms`,
                          }}
                        />
                      </div>
                      <div
                        className="size-4 rounded-full bg-contrast/7 shimmer shimmer-bg text-foreground shrink-0"
                        style={{ animationDelay: `${i * 120}ms` }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <AnimatePresence mode="wait" initial={false}>
                  {!usingNewCard ? (
                    <motion.div
                      key="saved-cards"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                      className="flex flex-col gap-1.5"
                    >
                      {savedMethods.map((pm) => {
                        const isSelected = pm.id === selectedPmId;
                        const isDeleting = deletingPmId === pm.id;
                        return (
                          <div
                            key={pm.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedPmId(pm.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedPmId(pm.id);
                              }
                            }}
                            className={cn(
                              "group flex items-center gap-2.5 rounded-md border h-12 px-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-colors cursor-pointer text-left outline-none",
                              isSelected
                                ? "border-contrast/30 bg-contrast/6"
                                : "border-contrast/10 bg-contrast/[0.02] hover:bg-contrast/5",
                            )}
                          >
                            <CardIcon brand={pm.brand} />
                            <span className="text-sm text-heading flex-1 truncate">
                              {formatBrand(pm.brand)} ending in {pm.last4}
                              <span className="text-contrast/40 ml-1.5">
                                Expires {String(pm.exp_month).padStart(2, "0")}/
                                {pm.exp_year}
                              </span>
                            </span>
                            <span className="size-4 shrink-0 flex items-center justify-center">
                              {isSelected && (
                                <Icon
                                  name="IconCircleCheck"
                                  fill="filled"
                                  size={16}
                                  className="text-white"
                                />
                              )}
                            </span>
                            <button
                              type="button"
                              aria-label="Remove card"
                              disabled={isDeleting}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCard(pm.id);
                              }}
                              className="flex items-center justify-center size-6 shrink-0 text-contrast/40 hover:text-destructive transition-colors cursor-pointer disabled:cursor-not-allowed"
                            >
                              <Icon
                                name={
                                  isDeleting
                                    ? "IconLoadingCircle"
                                    : "IconTrashCanSimple"
                                }
                                size={14}
                                className={cn(isDeleting && "animate-spin")}
                              />
                            </button>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => setShowNewCardInput(true)}
                        className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors w-fit cursor-pointer mt-0.5"
                      >
                        <Icon name="IconPlusSmall" size={14} />
                        Add another card
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="new-card"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                      className="flex flex-col gap-3"
                    >
                      {/* Full name on card */}
                      <Input
                        type="text"
                        variant="default"
                        value={billing.cardName}
                        onChange={(e) =>
                          updateBilling("cardName", e.target.value)
                        }
                        placeholder="Full name on card"
                      />

                      {/* Card number */}
                      <div className="h-9 rounded-md border border-contrast/[0.15] bg-contrast/6 px-3 flex items-center shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus-within:border-contrast/30 transition-colors">
                        <CardNumberElement
                          options={{
                            style: ELEMENT_STYLE,
                            showIcon: true,
                            placeholder: "1234 1234 1234 1234",
                          }}
                          className="w-full"
                          onChange={(e) => setCardComplete(e.complete)}
                        />
                      </div>

                      {/* Expiry + CVC */}
                      <div className="flex gap-3">
                        <div className="h-9 flex-1 rounded-md border border-contrast/[0.15] bg-contrast/6 px-3 flex items-center shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus-within:border-contrast/30 transition-colors">
                          <CardExpiryElement
                            options={{ style: ELEMENT_STYLE }}
                            className="w-full"
                          />
                        </div>
                        <div className="h-9 flex-1 rounded-md border border-contrast/[0.15] bg-contrast/6 px-3 flex items-center shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus-within:border-contrast/30 transition-colors">
                          <CardCvcElement
                            options={{ style: ELEMENT_STYLE }}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 shrink-0 px-6 py-4 border-t border-border">
          <SubmitButton
            type="button"
            variant="primary"
            size="sm"
            className="ml-auto"
            onClick={handleSubscribe}
            disabled={!canSubscribe}
            loading={isBusy}
            loadingLabel="Subscribing"
            icon={<Icon name="IconArrowRight" size={14} />}
          >
            Subscribe
          </SubmitButton>
        </div>
      </div>
    </>
  );
}

/* ─── Billing Period Select ─── */

function BillingPeriodSelect({
  cadence,
  onCadenceChange,
  annualPerSeatMonthly,
  monthlyPerSeat,
}: {
  cadence: "monthly" | "annual";
  onCadenceChange: (c: "monthly" | "annual") => void;
  annualPerSeatMonthly: number;
  monthlyPerSeat: number;
}) {
  const [open, setOpen] = useState(false);

  const opts: {
    value: "monthly" | "annual";
    title: string;
    price: number;
  }[] = [
    { value: "annual", title: "Annual", price: annualPerSeatMonthly },
    { value: "monthly", title: "Monthly", price: monthlyPerSeat },
  ];
  const selected = opts.find((o) => o.value === cadence) ?? opts[0];

  return (
    <Select.Root open={open} modal onOpenChange={setOpen}>
      <Select.Trigger
        size="default"
        variant="default"
        className="w-full justify-between bg-transparent"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Icon
            name="IconCalendarClock4"
            size={16}
            className="text-heading opacity-40 shrink-0"
          />
          <span className="text-sm text-heading shrink-0">
            {selected.title}
          </span>
          <span className="text-sm text-contrast/40 truncate">
            ${selected.price}/seat/mo
          </span>
        </div>
        <Select.Icon
          size={14}
          className={cn(
            "ml-0 opacity-50 transition-transform duration-300",
            open && "rotate-180",
          )}
        />
      </Select.Trigger>

      <Select.Content align="start">
        <Select.List>
          {opts.map((o) => (
            <Select.Item
              key={o.value}
              selected={o.value === cadence}
              onClick={() => {
                onCadenceChange(o.value);
                setOpen(false);
              }}
            >
              <Icon
                name="IconCalendarClock4"
                size={14}
                className={cn(
                  "shrink-0",
                  o.value === cadence
                    ? "text-[#ffaf53]"
                    : "text-heading opacity-40",
                )}
              />
              <Select.ItemLabel selected={o.value === cadence}>
                {o.title} · ${o.price}/seat/mo
              </Select.ItemLabel>
              {o.value === cadence && <Select.ItemCheck />}
            </Select.Item>
          ))}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}

/* ─── Seats Stepper (card style) ─── */

function SeatsStepper({
  seats,
  maxSeats,
  onSeatsChange,
}: {
  seats: number;
  maxSeats: number;
  onSeatsChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-md border border-contrast/[0.15] bg-transparent px-3.5 py-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
      <div className="flex flex-col min-w-0 gap-0.5">
        <div className="flex items-center gap-1.5">
          <Icon name="IconUser" size={14} className="text-[#FFD45C] shrink-0" />
          <span className="text-sm font-medium text-heading">Seats</span>
        </div>
        <p className="text-xs text-contrast/40 leading-4">
          Invite more users to your organization.
        </p>
      </div>

      <div className="flex items-center rounded-md border border-contrast/[0.15] overflow-hidden shrink-0 ml-2">
        <button
          type="button"
          onClick={() => onSeatsChange(Math.max(1, seats - 1))}
          disabled={seats <= 1}
          className="flex items-center justify-center size-[29px] bg-contrast/6 hover:bg-contrast/[0.12] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Icon name="IconMinusSmall" size={14} className="text-heading" />
        </button>
        <div className="flex items-center justify-center w-9 h-[29px] border-x border-contrast/[0.15] bg-contrast/[0.02]">
          <NumberFlow
            value={seats}
            className="text-sm font-medium text-heading tabular-nums"
          />
        </div>
        <button
          type="button"
          onClick={() => onSeatsChange(Math.min(maxSeats, seats + 1))}
          disabled={seats >= maxSeats}
          className="flex items-center justify-center size-[29px] bg-contrast/6 hover:bg-contrast/[0.12] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Icon name="IconPlusSmall" size={14} className="text-heading" />
        </button>
      </div>
    </div>
  );
}

/* ─── Card Brand Logo ─── */

function formatBrand(brand: string | null): string {
  if (!brand) return "Card";
  const names: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "Amex",
    discover: "Discover",
    diners: "Diners",
    jcb: "JCB",
    unionpay: "UnionPay",
  };
  return names[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
}

function brandToPaymentType(brand: string | null | undefined): PaymentType {
  switch ((brand ?? "").toLowerCase()) {
    case "visa":
      return "Visa";
    case "mastercard":
      return "Mastercard";
    case "amex":
    case "american express":
    case "americanexpress":
      return "Amex";
    case "discover":
      return "Discover";
    case "diners":
    case "diners club":
    case "dinersclub":
      return "Diners";
    case "jcb":
      return "Jcb";
    case "unionpay":
      return "Unionpay";
    case "maestro":
      return "Maestro";
    case "elo":
      return "Elo";
    default:
      return "Generic" as PaymentType;
  }
}

function CardIcon({ brand }: { brand: string | null | undefined }) {
  return (
    <PaymentIcon
      type={brandToPaymentType(brand)}
      format="flatRounded"
      width={28}
      className="shrink-0"
    />
  );
}
